import { isAxiosError, type AxiosError } from "axios";
import { isApiError, isApiResponseError, isNoNetworkError } from "@web-monorepo/fetchers";
import type { ApiErrorCode } from "@web-monorepo/api-types/apiErrorCodes";

// Reads an API failure whether it came from the axios clients or from @web-monorepo/fetchers, so
// callers keep working while marketplace moves off axios (WEBP-975). Axios is checked first:
// isApiResponseError also matches an AxiosError with a response, but axios keeps the body in
// `response.data`, not `response.body`.

export const DEFAULT_ERROR_MESSAGE =
  "An error has occurred. Please try again in a few minutes or you may send us a message.";

export interface ApiErrorBody {
  type?: string | number;
  message?: string;
  // A few endpoints send `{ error: string }` here instead; queryClient's constructMessage reads both.
  detail?: string;
  code?: ApiErrorCode;
  fallbackMessage?: string;
  expected?: boolean;
}

// Codes for an axios request that got no response. The axios errorHandler toasts and logs them,
// apart from its silent cases; callers that see them afterwards (the query client, the activity
// heartbeat) treat them as handled.
export const NETWORK_ERROR_CODES: ReadonlySet<string | undefined> = new Set(["ERR_NETWORK", "ECONNABORTED"]);

export const getApiErrorBody = (error: unknown): ApiErrorBody | undefined => {
  if (isAxiosError<{ error?: ApiErrorBody }>(error)) {
    return error.response?.data?.error;
  }
  if (isApiError(error)) {
    return (error.response?.body as { error?: ApiErrorBody } | undefined)?.error;
  }
  return undefined;
};

/** For the few endpoints whose error body is `{ error: string }` instead of the standard object. */
export const getApiErrorString = (error: unknown): string | undefined => {
  const body: unknown = getApiErrorBody(error);
  return typeof body === "string" ? body : undefined;
};

export const getApiErrorStatus = (error: unknown): number | undefined => {
  if (isAxiosError(error)) {
    return error.response?.status;
  }
  if (isApiResponseError(error)) {
    return error.response.status;
  }
  return undefined;
};

export const getApiErrorUrl = (error: unknown): string | undefined => {
  if (isAxiosError(error)) {
    const request: unknown = error.request;
    return typeof request === "object" &&
      request !== null &&
      "responseURL" in request &&
      typeof request.responseURL === "string"
      ? request.responseURL
      : error.config?.url;
  }
  if (isApiResponseError(error)) {
    return error.request?.url ?? error.response.req?.url;
  }
  return undefined;
};

/**
 * The request got no response: offline, DNS failure, or an axios timeout. Narrows to `Error` on the
 * fetchers side because isNoNetworkError also matches by name/message; check `instanceof
 * NoNetworkError` before reading its tags.
 */
export const isNetworkFailure = (error: unknown): error is AxiosError | Error => {
  if (isAxiosError(error)) {
    return !error.response && NETWORK_ERROR_CODES.has(error.code);
  }
  return error instanceof Error && isNoNetworkError(error);
};
